import { useCallback, useState } from 'react';
import { type KeyedMutator, useSWRConfig } from 'swr';
import { TrashIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components';
import type { ActionConfig } from '@/components/ActionBar';
import { ActionType } from '@/components/ActionBar';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { useGridMultiSelection } from '@/hooks';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import { type ReportEvent, type ReportWithEvents } from '@/types/report';

interface UseReportBulkRemovalResult {
    selectedIds: Set<string>;
    isMultiSelectMode: boolean;
    selectedCount: number;
    toggleSelected: (id: string) => void;
    enterMultiSelectMode: () => void;
    exitMultiSelectMode: () => void;
    toggleSelectAll: (visibleEvents: ReportEvent[]) => void;
    bulkRemoveModalOpen: boolean;
    openBulkRemoveModal: () => void;
    closeBulkRemoveModal: () => void;
    bulkRemoveInProgress: boolean;
    confirmBulkRemove: (eventIds: string[]) => Promise<void>;
    selectionBulkActions: ActionConfig[];
}

export function useReportBulkRemoval(
    reportId: string,
    mutate: KeyedMutator<ReportWithEvents>,
): UseReportBulkRemovalResult {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const { mutate: mutateCache } = useSWRConfig();
    const {
        selectedIds,
        isMultiSelectMode,
        selectedCount,
        toggleSelected,
        selectAll,
        clearSelection,
        enterMultiSelectMode,
        exitMultiSelectMode,
    } = useGridMultiSelection();
    const [bulkRemoveModalOpen, setBulkRemoveModalOpen] = useState(false);
    const [bulkRemoveInProgress, setBulkRemoveInProgress] = useState(false);

    const toggleSelectAll = useCallback(
        (visibleEvents: ReportEvent[]) => {
            const visibleIds = visibleEvents.map((event) => event.id);
            if (selectedCount === visibleIds.length && visibleIds.length > 0) {
                clearSelection();
            } else {
                selectAll(visibleIds);
            }
        },
        [selectedCount, clearSelection, selectAll],
    );

    const confirmBulkRemove = useCallback(
        async (eventIds: string[]) => {
            if (bulkRemoveInProgress || eventIds.length === 0) {
                return;
            }
            setBulkRemoveInProgress(true);
            try {
                const { deleted, failed } =
                    await EngineService.removeReportEvents(reportId, {
                        ids: eventIds,
                    });
                await Promise.all([
                    mutate(),
                    mutateCache(
                        (key) =>
                            Array.isArray(key) &&
                            key[0] === CACHE_KEYS.REPORTS_LIST,
                        undefined,
                        { revalidate: true },
                    ),
                ]);
                if (failed > 0 && deleted > 0) {
                    toast.error(
                        t('detail.removeEventsPartial', {
                            removed: deleted,
                            failed,
                        }),
                    );
                } else if (failed > 0) {
                    toast.error(t('detail.removeEventsError'));
                    return;
                } else {
                    toast.success(
                        t('detail.removeEventsSuccess', { count: deleted }),
                    );
                }
                setBulkRemoveModalOpen(false);
                exitMultiSelectMode();
            } catch {
                toast.error(t('detail.removeEventsError'));
            } finally {
                setBulkRemoveInProgress(false);
            }
        },
        [
            bulkRemoveInProgress,
            reportId,
            t,
            exitMultiSelectMode,
            mutate,
            mutateCache,
        ],
    );

    const selectionBulkActions: ActionConfig[] = [
        {
            type: ActionType.ITEM,
            icon: TrashIcon,
            label: t('detail.remove'),
            onClick: () => setBulkRemoveModalOpen(true),
        },
    ];

    return {
        selectedIds,
        isMultiSelectMode,
        selectedCount,
        toggleSelected,
        enterMultiSelectMode,
        exitMultiSelectMode,
        toggleSelectAll,
        bulkRemoveModalOpen,
        openBulkRemoveModal: () => setBulkRemoveModalOpen(true),
        closeBulkRemoveModal: () => setBulkRemoveModalOpen(false),
        bulkRemoveInProgress,
        confirmBulkRemove,
        selectionBulkActions,
    };
}
